import { chain, get, size } from "lodash";
import React, { useEffect } from "react";

import { Button } from "@shared/ui";
import EmptySearchMark from "../components/EmptySearchMark";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import ReportLink from "../components/report/ReportLink";
import { useReport, useReportArea } from "../components/report/ReportProvider";
import {
  formatAcceptedDocumentTypes,
  getIntegrationConfig,
} from "../lib/utility";

const combineAcceptedDocumentTypes = (session) => {
  const combinedAcceptedDocumentTypes = chain(session)
    .get("config.documentation.acceptedDocs") // Get accepted docs config
    .map("displayName") // Select display names
    .thru((names) => {
      const last = names.pop();
      // If there are exactly two items, join them with " or " without a comma
      return names.length === 1
        ? names[0] + " or " + last
        : names.length
        ? names.join(", ") + ", or " + last
        : last;
    })
    .value();

  return combinedAcceptedDocumentTypes; // Output: "Declarations Page or Policy Renewal, or ID Card"
};

const StepTransition = ({
  client,
  session,
  nextStep,
  step,
  previousStep,
  loginInformation,
  retryCount,
  setRetryCount,
  posthog,
  setLoginInformation,
  history,
  setHistory,
  partnerCarrierConfig,
  setIntegration,
  integration,
}) => {
  // With the report link showing on a document step, the layout is tighter so the screen fits without scrolling.
  // The max-login-attempts step keeps its own layout.
  const reportArea = useReportArea(step);
  const reporting = useReport();
  // Support only helps when the carrier has a reset link; otherwise go straight to login.
  const hasResetLink =
    step === "max-login-attempts" &&
    Boolean(
      get(
        getIntegrationConfig(session, loginInformation, integration),
        "recoveryUrl"
      )
    );

  const handleSessionExpiredSecondary = () => {
    return retryCount > 1
      ? {
          text: `${
            get(session, "config.manual.enabled") ? "Verify manually" : "Exit"
          }`,
          step: `${
            get(session, "config.manual.enabled") ? "manual-account" : "exit"
          }`,
        }
      : undefined;
  };

  useEffect(() => {
    // Coming back from the report page is not a new page view.
    if (!reporting?.returnedFromReport) {
      posthog.capture("$pageview", {
        step,
        carrier: get(loginInformation, "carrier.id"),
      });
    }
    switch (step) {
      case "session-expired": {
        setRetryCount(retryCount + 1);
        return;
      }
      default:
        return;
    }
  }, [step, loginInformation]);

  const isExitShortcut = get(
    session,
    "config.manual.exit-confirmation-shortcut",
    false
  );

  const carrierName = get(loginInformation, "carrier.name");

  // Only defined carriers can log in; "My carrier isn't listed" leaves none.
  const loginToImport = get(loginInformation, "carrier.defined")
    ? { text: "Login to import documents instantly", step: "login" }
    : undefined;

  const pages = {
    "no-policies": {
      illustration: "search",
      header: "No policies found",
      subheader: `We didn’t find any compatible policies on your ${
        carrierName ? `${carrierName} ` : ""
      }account.`,
      primary: {
        text:
          get(session, "config.no-policies.primary.text") ??
          "Try a different account",
        step: "carriers",
      },
      secondary: {
        text: `${
          get(session, "config.no-policies.secondary.text") ??
          (get(session, "config.manual.enabled") ? "Verify manually" : "Exit")
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "carrier-maintenance": {
      illustration: "manual-required",
      header: "Please try again later",
      subheader: `${get(
        loginInformation,
        "carrier.name",
        "Your carrier"
      )}'s services are currently undergoing maintenance. We apologize for the inconvenience.`,
      primary: {
        text: "Continue",
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
      secondary: {
        text: "Try Again",
        step: previousStep === "enterMfa" ? "login" : previousStep,
      },
    },
    "declaration-page-sample": {
      illustration: "declaration-sample",
      header: "How can I identify my declarations page?",
      subheader: `You can get your declarations page by logging into your insurance provider's website or contacting your agent. Look for key information like coverage types, policy numbers, and premium amounts.`,
      primary: {
        text: "Upload Document",
        step: previousStep,
      },
      secondary: loginToImport,
      isLeftAligned: true,
    },
    "id-card-sample": {
      illustration: "id-card-sample",
      header: "How can I identify an ID card?",
      subheader: `Your insurance ID card is a small card (physical or digital) provided by your insurance provider. It typically displays your policy number, effective date, expiration date and your insurer's contact information. You can find it in insurance provider's mobile app, or by contacting your agent.`,
      primary: {
        text: "Upload Document",
        step: previousStep,
      },
      secondary: loginToImport,
      isLeftAligned: true,
    },
    "session-expired": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader:
        "Your session has timed out. Please log in again to continue verification.",
      primary: {
        text: "Return to login",
        step: "login",
      },
      secondary: handleSessionExpiredSecondary(),
    },
    "exit-confirmation": {
      illustration: "exit-confirmation",
      isCentered: true,
      header: "Are you sure?",
      subheader:
        get(session, "config.exit-confirmation.subheader") ??
        `${client.displayName} requires insurance verification to access its services. Declining verification through Axle may result in service delays.`,
      primary: {
        text:
          get(session, "config.exit-confirmation.primary.text") ??
          "Finish verification",
        step: previousStep,
      },
      secondary: {
        text: isExitShortcut
          ? "Upload manually"
          : get(session, "config.exit-confirmation.secondary.text") ?? "Exit",
        step: isExitShortcut
          ? "manual-account"
          : get(session, "config.exit-survey.enabled", true) // Defaults to true even if exit-survey config is not set
          ? "exit-survey"
          : "exit",
      },
      ...(isExitShortcut && {
        tertiary: {
          text:
            get(session, "config.exit-confirmation.secondary.text") ?? "Exit",
          step: get(session, "config.exit-survey.enabled", true) // Defaults to true even if exit-survey config is not set
            ? "exit-survey"
            : "exit",
        },
      }),
    },
    "max-server-login-attempts": {
      illustration: "max-login-attempts",
      header: "You have reached the maximum login attempts.",
      subheader: `The carrier may be experiencing issues, and to avoid locking your ${get(
        loginInformation,
        "carrier.name"
      )} account, please share your insurance using an alternate option.`,
      primary: {
        text: get(session, "config.manual.enabled")
          ? "Verify manually"
          : "Continue",
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "failed"
        }`,
      },
      secondary: {
        text: "Exit",
        step: "failed",
      },
    },
    "max-login-attempts": {
      illustration: "max-login-attempts",
      header: "You have reached the maximum login attempts.",
      subheader: `To avoid locking or suspending your ${get(
        loginInformation,
        "carrier.name"
      )} account, please reset your login information before trying again.`,
      primary: hasResetLink
        ? { text: "Reset my login information", step: "login-support" }
        : { text: "I have reset my login information", step: "login" },
      secondary: {
        text: "Verify manually",
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "max-link-attempts": {
      illustration: "max-login-attempts",
      header: "You have reached the maximum lookup attempts.",
      subheader: `Looks like you are having trouble with your policy details. Please try to login to your ${get(
        loginInformation,
        "carrier.name"
      )} account or verify manually.`,
      primary: {
        text: "Login to my account",
        step: "login",
      },
      secondary: {
        text: `${
          get(session, "config.manual.enabled") ? "Verify manually" : "exit"
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "aaa-chapter-error": {
      illustration: "not-found",
      header: "Hmm. Something’s not right.",
      subheader: "We're unable to connect to your AAA regional club right now.",
      primary: {
        text: `${
          get(session, "config.manual.enabled")
            ? "Verify manually"
            : "Try a different club or carrier"
        }`,
        step: `${
          get(session, "config.manual.enabled")
            ? "manual-account"
            : previousStep
        }`,
      },
      secondary: {
        text: "Exit",
        step: "exit",
      },
    },
    "account-pending": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader:
        "Sorry, your insurance account or policy has not been fully set up for online access. Please login to your account to finish setup.",
      primary: {
        text: "I've set up my insurance account",
        step: "login",
      },
      secondary: {
        text: `${
          get(session, "config.manual.enabled") ? "Verify manually" : "Exit"
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "mfa-enrollment-required": {
      illustration: "manual-required",
      header: "Hmm. Something’s not right.",
      subheader:
        "MFA enrollment is required for your insurance account. Please enroll in MFA and try again.",
      primary: {
        text: "I've set up my MFA enrollment",
        step: "login",
      },
      secondary: {
        text: `${
          get(session, "config.manual.enabled") ? "Verify manually" : "Exit"
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "policy-pending": {
      illustration: "manual-required",
      header: "Sorry, your policy is being processed or is missing data.",
      subheader: `Please ensure policy details are available in your account, and if not, contact ${get(
        loginInformation,
        "carrier.name"
      )}.`,
      secondary: {
        text: "Try again",
        step: "login",
      },
      primary: {
        text: `${
          get(session, "config.manual.enabled") ? "Verify manually" : "Exit"
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "policy-not-active": {
      illustration: "not-found",
      header: "Policy is not active",
      subheader:
        "We've identified that your policy is not currently active. Please ensure your policy is up to date and try again later.",
      primary: {
        text: "Try a different policy",
        step: "carriers",
      },
      secondary: {
        text: `${
          get(session, "config.manual.enabled") ? "Verify manually" : "Exit"
        }`,
        step: `${
          get(session, "config.manual.enabled") ? "manual-account" : "exit"
        }`,
      },
    },
    "redirect-login": {
      illustration: "not-found",
      header: `This is a ${get(partnerCarrierConfig, "carrier.name")} policy`,
      subheader: `Click continue to finish logging in through ${get(
        partnerCarrierConfig,
        "carrier.name"
      )}`,
      primary: {
        text: "Continue",
        step: "login",
      },
    },
    /* ===========================
     * Enhanced Manual Errors - START
     * ===========================
     */
    "unsupported-document": {
      illustration: "not-found",
      header: "Hmm. Something’s not right.",
      subheader:
        "Please make sure to upload an image or pdf document with your policy information clearly legible.",
      primary: {
        text: "Try again",
        step: previousStep,
      },
      secondary: {
        text: "Exit",
        step: get(session, "config.exit-survey.enabled")
          ? "exit-survey"
          : "exit",
      },
    },
    "document-missing-fields": {
      illustration: "not-found",
      header: "Upload a new document",
      subheader:
        "The document you uploaded does not contain the necessary insurance information. Please upload a complete and legible insurance document.",
      primary: {
        text: "Try again",
        step: previousStep,
      },
      secondary: {
        text: "Exit",
        step: get(session, "config.exit-survey.enabled")
          ? "exit-survey"
          : "exit",
      },
    },
    "document-type-not-supported": {
      illustration: "not-found",
      header: "Hmm. Something’s not right.",
      subheader: `The document you uploaded is not supported. Please try again with ${formatAcceptedDocumentTypes(
        get(session, "config.documentation.acceptedDocs")
      )}.`,
      primary: {
        text: "Try again",
        step: previousStep,
      },
      secondary: {
        text: "Exit",
        step: get(session, "config.exit-survey.enabled")
          ? "exit-survey"
          : "exit",
      },
    },
    "text-validation-failed": {
      illustration: "not-found",
      header: "Hmm. Something’s not right.",
      subheader:
        "The document you uploaded does not contain the necessary insurance information. Please upload a complete and legible insurance document.",
      primary: {
        text: "Try again",
        step: previousStep,
      },
      secondary: {
        text: "Exit",
        step: get(session, "config.exit-survey.enabled")
          ? "exit-survey"
          : "exit",
      },
    },
    /* ===========================
     * Enhanced Manual Errors - END
     * ===========================
     */
    "backup-document-required": {
      illustration: "not-found",
      header: "Policy document needed",
      subheader: `We couldn't retrieve all of the necessary details from your insurance carrier. Please upload your ${combineAcceptedDocumentTypes(
        session
      )} to finish sharing your information.`,
      primary: {
        text: "Upload document",
        step: "manual-policy-document",
      },
    },
  };

  const isSearch = pages[step].illustration === "search";
  // Search results and exit confirmation sit vertically centered; the rest start near the top.
  const isCentered = isSearch || get(pages, `${step}.isCentered`, false);

  return (
    <>
      <StepBody
        centered={isCentered}
        className={
          reportArea && step !== "max-login-attempts"
            ? "grow gap-4 pt-6"
            : isSearch
            ? "gap-4"
            : isCentered
            ? undefined
            : "pt-8"
        }
      >
        <div className="flex justify-center">
          {isSearch ? (
            <EmptySearchMark className="mb-4" />
          ) : (
            <div
              aria-hidden
              style={{
                backgroundImage: `url("./icons/${pages[step].illustration}.svg")`,
              }}
              className={`flex-none h-32 w-full bg-contain bg-no-repeat bg-center`}
            ></div>
          )}
        </div>
        <StepHeading
          title={pages[step].header}
          description={pages[step].subheader}
          align={get(pages, `${step}.isLeftAligned`) ? "left" : "center"}
        />
      </StepBody>
      <StepFooter>
        {pages[step].primary && (
          <Button
            onClick={() => {
              if (step === "redirect-login") {
                // Take off the last two elements in history, so that carriers is the most recent step
                history.splice(size(history) - 2, 2);

                // set integration to partner carrier
                setIntegration(partnerCarrierConfig.carrier.id);

                setHistory(history);
                // Set loginInformation to partner carrier
                setLoginInformation(partnerCarrierConfig);
              }
              nextStep(pages[step].primary.step);
            }}
          >
            {pages[step].primary.text}
          </Button>
        )}
        {pages[step].secondary && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => nextStep(pages[step].secondary.step)}
          >
            {pages[step].secondary.text}
          </Button>
        )}
        {pages[step].tertiary && (
          <Button
            type="button"
            variant="text"
            onClick={() => nextStep(pages[step].tertiary.step)}
          >
            {pages[step].tertiary.text}
          </Button>
        )}
        {reportArea && <ReportLink area={reportArea} step={step} />}
      </StepFooter>
    </>
  );
};

export default StepTransition;
